import { graphMailService, type Email } from "../../services/graphMailService";
import {
    mailLinkService,
    type MailLinkBatchItemInput,
} from "../../services/mailLinkService";
import { baueVerlaufBatch, mitgenommeneMeldung, verlaufMeldung } from "./mailVerlaufItems";
import type { MailVorgangZiel } from "./mailVorgangAssign";

/**
 * Den Verlauf einer Mail an EIN Ziel hängen — die Klammer, die sich das
 * Detailfenster und das Kontextmenü der Liste teilen.
 *
 * Sie ist bewusst dünn: Zusammenstellen und Beurteilen liegen pur in
 * `mailVerlaufItems.ts`, hier kommen nur die beiden I/O-Wege dazu (Verlauf
 * holen, Batch schicken). Damit gibt es keine zweite Fassung, die auseinander
 * driften könnte.
 *
 * Der Verlauf wird NACHGELADEN, wenn der Aufrufer keinen mitbringt: Ob die
 * Ansicht den Thread gerade aufgefächert zeigt, ist eine Frage der Darstellung
 * — die Zuweisung gilt trotzdem dem ganzen Schriftwechsel. Der Aufruf ist
 * billig, `fetchConversation` hält das Ergebnis 30 Sekunden im Cache.
 *
 * Scheitert das Nachladen, wird trotzdem verlinkt: dann eben nur die geöffnete
 * Nachricht. Eine Zuweisung darf nicht an einem Nebenweg scheitern.
 */
export async function bindeVerlaufAnZiel(args: {
    mailbox: string;
    /** Snapshot der geöffneten Nachricht — die Zuweisung, die der Nutzer meint. */
    offen: MailLinkBatchItemInput;
    conversationId: string | null;
    ziel: MailVorgangZiel;
    /** Bereits geladener Verlauf (Detailfenster); sonst wird nachgeladen. */
    verlauf?: readonly Email[] | null;
    istImPosteingang: (email: Email) => boolean;
    /** T-29 — Einzelansicht: auch bei Erfolg sagen, wie viele Mails mitgewandert sind. */
    mitgenommeneNennen?: boolean;
    /**
     * T-29 — wie `mitgenommeneNennen`, aber der Satz geht als eigener Hinweis
     * an diesen Empfänger statt in den Rückgabewert. Der Rückgabewert bleibt
     * dann den Problemen vorbehalten (Teilfehler, gekappte Überlänge), damit
     * der Aufrufer eine Erfolgsmeldung anders zeigen kann als einen Fehler.
     */
    meldeMitgenommene?: (text: string) => void;
}): Promise<string | null> {
    const { mailbox, offen, conversationId, ziel, istImPosteingang } = args;

    let verlauf: readonly Email[] = args.verlauf ?? [];
    if (verlauf.length === 0 && conversationId) {
        verlauf = await graphMailService
            .fetchConversation(mailbox, conversationId)
            .catch(() => [] as Email[]);
    }

    const plan = baueVerlaufBatch(mailbox, offen, verlauf, istImPosteingang);
    const { results } = await mailLinkService.createBatch({
        ...(ziel.vorgangId != null ? { vorgangId: ziel.vorgangId } : {}),
        ...(ziel.todoId != null ? { todoId: ziel.todoId } : {}),
        archiveInbox: true,
        items: plan.items,
    });

    // Hat der Batch etwas verschoben, stimmt die Ordner-Sicht des Postfachs
    // nicht mehr. Die geöffnete Mail selbst ist nicht dabei — sie archiviert
    // der angestammte Weg, der seine Listen ohnehin auffrischt.
    if (plan.items.some((i) => i.isInInbox)) {
        graphMailService.invalidateMailbox(mailbox);
    }

    const meldung = verlaufMeldung(results, plan.ausgelassen);
    if (args.meldeMitgenommene) {
        const mitgenommen = mitgenommeneMeldung(results, offen.messageId);
        if (mitgenommen) args.meldeMitgenommene(mitgenommen);
        return meldung;
    }
    if (!args.mitgenommeneNennen) return meldung;
    const mitgenommen = mitgenommeneMeldung(results, offen.messageId);
    return [mitgenommen, meldung].filter(Boolean).join(" ") || null;
}
